import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import { Shield, Trash2, TriangleAlert, Check } from 'lucide-react';
import { auth } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = import.meta.env.VITE_API_URL as string
import {
    privacySettingsStyles,
    privacySettingsShieldColor,
    privacySettingsWarningIconColor,
} from './PrivacySettings.styles';

const PrivacySettings: React.FC = () => {
    const { t } = useTranslation();
    const { user, updateMarketingConsent, logout } = useAuth();
    const [marketingConsent, setMarketingConsent] = useState(user?.marketingConsent ?? false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteReason, setDeleteReason] = useState(''); // TODO deleteReason se niak nezpracovává
    const [isSaved, setIsSaved] = useState(false);
    const [deletingAccount, setDeletingAccount] = useState(false);
    const [deleteError, setDeleteError] = useState('');

    const handleSave = async () => {
        await updateMarketingConsent(marketingConsent);
        setIsSaved(true);
        setTimeout(() => setIsSaved(false), 3000);
    };

    const handleDeleteAccount = async () => {
        if (!user) return;
        setDeleteError('');
        setDeletingAccount(true);
        try {
            const token = await auth.currentUser?.getIdToken();
            const res = await fetch(`${API_URL}/api/users/${user.uid}/status`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ status: 'requestDelete' }),
            });
            if (!res.ok) {
                const body = await res.json().catch(() => ({})) as { message?: string };
                throw new Error(body.message || t('privacySettings.dangerZone.requestFailed'));
            }
            setShowDeleteModal(false);
            await logout();
        } catch (err) {
            setDeleteError(err instanceof Error ? err.message : t('privacySettings.dangerZone.requestFailed'));
        } finally {
            setDeletingAccount(false);
        }
    };

    return (
        <Box sx={privacySettingsStyles.root}>
            <Box>
                <Typography variant="h4" fontWeight={700}>{t('privacySettings.title')}</Typography>
                <Typography color="text.secondary" mt={0.5}>{t('privacySettings.subtitle')}</Typography>
            </Box>

            {/* Marketing Consent */}
            <Paper elevation={0} sx={privacySettingsStyles.card}>
                <Box sx={privacySettingsStyles.cardHeader}>
                    <Typography variant="subtitle1" fontWeight={600} sx={privacySettingsStyles.headerTitle}>
                        <Shield size={20} color={privacySettingsShieldColor} /> {t('privacySettings.marketing.title')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">{t('privacySettings.marketing.description')}</Typography>
                </Box>
                <Box sx={privacySettingsStyles.cardBody}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={marketingConsent}
                                onChange={(e) => setMarketingConsent(e.target.checked)}
                                color="primary"
                            />
                        }
                        label={
                            <Box>
                                <Typography variant="body2" fontWeight={500}>{t('privacySettings.marketing.switchLabel')}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {t('privacySettings.marketing.switchDescription')}
                                </Typography>
                            </Box>
                        }
                        sx={privacySettingsStyles.marketingSwitch}
                    />
                    <Box sx={privacySettingsStyles.saveButtonWrap}>
                        <Button
                            variant="contained"
                            onClick={handleSave}
                            startIcon={isSaved ? <Check size={20} /> : undefined}
                            color={isSaved ? 'success' : 'primary'}
                        >
                            {isSaved ? t('privacySettings.marketing.saved') : t('privacySettings.marketing.saveButton')}
                        </Button>
                    </Box>
                </Box>
            </Paper>

            {/* GDPR Info */}
            <Paper elevation={0} sx={privacySettingsStyles.card}>
                <Box sx={privacySettingsStyles.cardHeader}>
                    <Typography variant="subtitle1" fontWeight={600}>{t('privacySettings.gdpr.title')}</Typography>
                </Box>
                <Box sx={privacySettingsStyles.gdprBody}>
                    <Typography variant="body2" color="text.secondary">{t('privacySettings.gdpr.text1')}</Typography>
                    <Typography variant="body2" color="text.secondary">{t('privacySettings.gdpr.text2')}</Typography>
                    <Typography variant="body2" color="text.secondary">{t('privacySettings.gdpr.text3')}</Typography>
                    {/* TODO: replace href="#" with a real GDPR policy URL */}
                    <Typography
                        component="a"
                        href="#"
                        variant="body2"
                        color="primary.main"
                        fontWeight={500}
                        sx={privacySettingsStyles.gdprLink}
                    >
                        {t('privacySettings.gdpr.policyLink')}
                    </Typography>
                </Box>
            </Paper>

            {/* Danger Zone */}
            <Paper elevation={0} sx={privacySettingsStyles.dangerCard}>
                <Box sx={privacySettingsStyles.dangerHeader}>
                    <Typography variant="subtitle1" fontWeight={600} color="error.main" sx={privacySettingsStyles.headerTitle}>
                        <TriangleAlert size={20} /> {t('privacySettings.dangerZone.title')}
                    </Typography>
                </Box>
                <Box sx={privacySettingsStyles.cardBody}>
                    <Typography variant="subtitle2" fontWeight={600}>{t('privacySettings.dangerZone.deleteTitle')}</Typography>
                    <Typography variant="body2" color="text.secondary" mt={1} mb={2} maxWidth={520}>
                        {t('privacySettings.dangerZone.deleteDescription')}
                    </Typography>
                    <Button
                        variant="outlined"
                        color="error"
                        startIcon={<Trash2 size={20} />}
                        onClick={() => setShowDeleteModal(true)}
                    >
                        {t('privacySettings.dangerZone.deleteButton')}
                    </Button>
                </Box>
            </Paper>

            {/* Delete Dialog */}
            <Dialog open={showDeleteModal} onClose={() => setShowDeleteModal(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={privacySettingsStyles.dialogTitle}>
                    <Box sx={privacySettingsStyles.dialogIconWrap}>
                        <TriangleAlert color={privacySettingsWarningIconColor} />
                    </Box>
                    {t('privacySettings.deleteDialog.title')}
                </DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={privacySettingsStyles.dialogAlert}>
                        {t('privacySettings.deleteDialog.warning')}
                    </Alert>
                    {deleteError && (
                        <Alert severity="error" sx={{ mb: 2 }}>{deleteError}</Alert>
                    )}
                    <TextField
                        label={t('privacySettings.deleteDialog.reasonLabel')}
                        multiline
                        rows={3}
                        fullWidth
                        value={deleteReason}
                        onChange={(e) => setDeleteReason(e.target.value)}
                    />
                </DialogContent>
                <DialogActions sx={privacySettingsStyles.dialogActions}>
                    <Button onClick={() => setShowDeleteModal(false)} disabled={deletingAccount}>{t('common.cancel')}</Button>
                    <Button variant="contained" color="error" onClick={handleDeleteAccount} disabled={deletingAccount}>
                        {deletingAccount ? t('common.processing') : t('privacySettings.deleteDialog.confirmButton')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default PrivacySettings;
